import { WallLateralDatumAnchor } from "building-types/datums/datumsTypesEnum";
import type { RoofWallFaces } from "building-types/roofs/generatedRoofSystemTypes";
import type { RoofGenerationStageInput, RoofGenerationStageResult } from "building-types/roofs/roofGenerationTypes";
import { GableRoofType, RoofStructureType } from "building-types/settings/settingsTypesEnum";
import { DEFAULT_VAULTED_ROOF_THICKNESS } from "doc/utils/roofTools";
import type { Face } from "geom/brep";
import { planeNewXY } from "geom/plane";
import { getEaveDepth } from "../../../elements/roofTrimUtility";
import { makeRoofBottomFaces } from "../../roofTools/makeRoofBottomFaces";

/**
 *
 */
export function generateRoofBottomFaces({
  subRoofOptions,
  subRoof,
}: RoofGenerationStageInput): RoofGenerationStageResult {
  const { roofVolume } = subRoof;
  const {
    roofSettings,
    baseHeight,
    roofType,
    heelHeightOffsets,
    extrusion,
    rails2D,
    startFaces,
    endFaces,
    rectangularOverhangs,
    extensions,
    edges,
  } = roofVolume;

  let { wallFaces } = subRoof;

  const { issues, inputPerimeters, roofInputSettings, ceilingHeight = baseHeight } = subRoofOptions;

  const { wallSettings } = roofInputSettings;

  const { limitPerimeter, bottomPerimeter } = inputPerimeters;
  const { leftRail, rightRail, peakRailNoExtension } = rails2D;
  const { face: startFrontFace } = startFaces;
  const { face: endFrontFace } = endFaces;
  const { hasStartExtension, hasEndExtension } = extensions;

  const { startOverhang, endOverhang, leftOverhang, rightOverhang } = rectangularOverhangs;

  const {
    structureType = RoofStructureType.FLAT,
    vaultedRoofThickness = DEFAULT_VAULTED_ROOF_THICKNESS,
    vaultedRoofRightThickness = vaultedRoofThickness,
    overhangDatum = WallLateralDatumAnchor.OUTSIDE_FACE_OF_FRAMING,
  } = roofSettings;

  const ceilingPlane = planeNewXY(0, 0, ceilingHeight + heelHeightOffsets.refOffset - getEaveDepth(roofSettings));

  // make bottom faces
  let bottomFaces: Face[] = [];
  let vaultedRoofCapFaces: RoofWallFaces[] = [];
  if (!limitPerimeter) {
    const bottomFacesResult = makeRoofBottomFaces({
      id: subRoof.id,
      leftRail: roofType === GableRoofType.RIGHT_SIDE_SHED_ROOF ? peakRailNoExtension : leftRail,
      rightRail: roofType === GableRoofType.LEFT_SIDE_SHED_ROOF ? peakRailNoExtension : rightRail,
      basePlane: ceilingPlane,
      leftRoofOverhang: leftOverhang,
      rightRoofOverhang: rightOverhang,
      startRoofOverhang: startOverhang,
      endRoofOverhang: endOverhang,
      edges,
      wallSettings,
      bottomPerimeter,
      hasStartExtension,
      hasEndExtension,
      extrusion,
      startFrontFace,
      endFrontFace,
      structureType,
      vaultedRoofThickness,
      vaultedRoofRightThickness,
      overhangDatum,
      issues,
    });

    bottomFaces = bottomFacesResult.bottomFaces;
    vaultedRoofCapFaces = bottomFacesResult.vaultedRoofCapFaces;
  }

  // add vaulted roof faces
  wallFaces = {
    ...wallFaces,
    vaultedRoofCapFaces,
  };

  return {
    subRoof: {
      bottomFaces,
      wallFaces,
    },
  };
}
